import { boundsOf, fitMercator, type LatLon } from '@trips/shared/geo';
import type { TripLeg } from '@trips/shared/trip-model';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import s from './MiniMap.module.css';
import { arcPath, type Pt, RouteArc } from './RouteArc';

/**
 * An SVG map drawn from a bundled 1:110m world outline: no tiles, no WebGL, works offline.
 * Used for trip covers and card thumbnails; the interactive maps come later with MapLibre.
 */

interface LandFile {
  rings: number[][];
}

let landPromise: Promise<LandFile> | null = null;
let landCache: LandFile | null = null;

function loadLand(): Promise<LandFile> {
  landPromise ??= fetch(`${import.meta.env.BASE_URL}geo/land-110m.json`)
    .then((r) => r.json() as Promise<LandFile>)
    .then((f) => {
      landCache = f;
      return f;
    });
  return landPromise;
}

function useLand(enabled: boolean): LandFile | null {
  const [land, setLand] = useState<LandFile | null>(landCache);
  useEffect(() => {
    if (!enabled || land) return;
    let alive = true;
    loadLand()
      .then((f) => alive && setLand(f))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [enabled, land]);
  return land;
}

/**
 * A coastline that crosses the 180° line (Russia, Fiji) jumps from 180 to -180 in the data.
 * Drawn as is, that line runs across the whole map and fills the oceans as land; keeping the
 * longitudes continuous (190 instead of -170) draws it as one shape.
 */
const continuousRings = new WeakMap<number[], number[]>();
function continuous(ring: number[]): number[] {
  const hit = continuousRings.get(ring);
  if (hit) return hit;
  const out = ring.slice();
  let shift = 0;
  for (let i = 2; i < out.length; i += 2) {
    const step = ring[i]! - ring[i - 2]!;
    if (step > 180) shift -= 360;
    else if (step < -180) shift += 360;
    out[i] = ring[i]! + shift;
  }
  continuousRings.set(ring, out);
  return out;
}

/** Keeps longitudes near the first point so routes across the Pacific don't wrap. */
function unwrapNear(ref: number, lon: number) {
  let l = lon;
  while (l - ref > 180) l -= 360;
  while (l - ref < -180) l += 360;
  return l;
}

interface MiniMapProps {
  legs: TripLeg[];
  stops?: LatLon[];
  width: number;
  height: number;
  variant?: 'cover' | 'card' | 'thumb';
  /** Draw the route arcs on mount. */
  draw?: boolean;
  labels?: boolean;
  className?: string;
  /** Leave out routes, stops and codes, for a caller that draws its own (trip playback). */
  bare?: boolean;
  /** Points the view is framed on instead of the legs, to hold one view while legs change. */
  frame?: LatLon[];
  /** Extra drawing on top, given the map's projection. */
  overlay?: (proj: (p: LatLon) => Pt, size: { width: number; height: number }) => ReactNode;
}

/** Last measured size per kind of map, so a screen's maps render at the right size at once. */
const knownSizes = new Map<string, { w: number; h: number }>();

/** Projected land outlines by view, so screens coming back don't redraw the world. */
const landPaths = new Map<string, string>();

const NO_STOPS: LatLon[] = [];

/**
 * Size of an element, kept current as it resizes. Measured by a ResizeObserver after layout
 * rather than during render: forcing layout mid-navigation cost about 35 ms per screen.
 */
function useBoxSize(key: string, enabled: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState(() => knownSizes.get(key) ?? null);
  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return;
    const ro = new ResizeObserver(([entry]) => {
      const box = entry?.contentRect;
      if (!box || box.width <= 0 || box.height <= 0) return;
      const next = { w: Math.round(box.width), h: Math.round(box.height) };
      knownSizes.set(key, next);
      // Before this frame paints, so there's no flash at the first guess.
      flushSync(() => setSize((prev) => (prev?.w === next.w && prev.h === next.h ? prev : next)));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [key, enabled]);
  return { ref, size };
}

export function MiniMap({
  legs,
  stops = NO_STOPS,
  width: w0,
  height: h0,
  variant = 'card',
  draw,
  labels,
  className,
  bare,
  overlay,
  frame,
}: MiniMapProps) {
  const measured = useBoxSize(`${variant}:${w0}x${h0}`, variant !== 'thumb');
  const width = measured.size?.w ?? w0;
  const height = measured.size?.h ?? h0;
  const land = useLand(true);

  const geom = useMemo(() => {
    const ref = frame?.[0]?.lon ?? legs[0]?.from.lon ?? stops[0]?.lon ?? 0;
    const pts: LatLon[] = [];
    if (frame?.length) for (const p of frame) pts.push({ lat: p.lat, lon: unwrapNear(ref, p.lon) });
    else
      for (const l of legs) {
        pts.push(
          { lat: l.from.lat, lon: unwrapNear(ref, l.from.lon) },
          { lat: l.to.lat, lon: unwrapNear(ref, l.to.lon) },
        );
      }
    if (!frame?.length)
      for (const p of stops) pts.push({ lat: p.lat, lon: unwrapNear(ref, p.lon) });
    if (pts.length === 0) return null;
    const b = boundsOf(pts);
    const minSpan = variant === 'thumb' ? 6 : 10;
    const project = fitMercator(b, width, height, variant === 'thumb' ? 0.16 : 0.2, minSpan);
    const proj = (p: LatLon): Pt => project({ lat: p.lat, lon: unwrapNear(ref, p.lon) });
    // Visible longitude window, to pick which copies of the land to draw.
    const pad = Math.max(30, (b.maxLon - b.minLon) * 1.5);
    const key = `${variant}|${width}|${height}|${b.minLat}|${b.maxLat}|${b.minLon}|${b.maxLon}`;
    return { proj, project, lonMin: b.minLon - pad, lonMax: b.maxLon + pad, ref, key };
  }, [legs, stops, width, height, variant, frame]);

  const landPath = useMemo(() => {
    if (!land || !geom) return '';
    const cached = landPaths.get(geom.key);
    if (cached !== undefined) return cached;
    let d = '';
    for (const raw of land.rings) {
      const ring = continuous(raw);
      let rMin = Infinity;
      let rMax = -Infinity;
      let latMax = -90;
      for (let i = 0; i < ring.length; i += 2) {
        rMin = Math.min(rMin, ring[i]!);
        rMax = Math.max(rMax, ring[i]!);
        latMax = Math.max(latMax, ring[i + 1]!);
      }
      // Antarctica wraps the whole globe and draws stray lines when shifted; it's never in view.
      if (latMax < -55) continue;
      for (const off of [-360, 0, 360]) {
        if (rMax + off < geom.lonMin || rMin + off > geom.lonMax) continue;
        for (let i = 0; i < ring.length; i += 2) {
          // Straight projection: the copies at ±360 handle wrapping, and wrapping each point
          // toward the route would tear shapes that reach the far side (Eurasia from the US).
          const [x, y] = geom.project({ lon: ring[i]! + off, lat: ring[i + 1]! });
          d += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
        }
        d += 'Z';
      }
    }
    landPaths.set(geom.key, d);
    if (landPaths.size > 80) landPaths.delete(landPaths.keys().next().value as string);
    return d;
  }, [land, geom]);

  if (!geom)
    return (
      <div
        ref={measured.ref}
        className={`${s.empty} ${className ?? ''}`}
        style={{ aspectRatio: `${w0} / ${h0}` }}
      />
    );

  const stroke = variant === 'thumb' ? 1.25 : variant === 'cover' ? 2 : 1.75;
  // Each code sits on the side away from its routes, so a line never runs through it.
  const codes = new Map<string, { at: Pt; code: string; pull: number }>();
  const addCode = (code: string | null | undefined, at: Pt, other: Pt) => {
    if (!code) return;
    const c = codes.get(code) ?? { at, code, pull: 0 };
    c.pull += other[1] - at[1];
    codes.set(code, c);
  };
  for (const l of legs) {
    const a = geom.proj(l.from);
    const b = geom.proj(l.to);
    addCode(l.from.code, a, b);
    addCode(l.to.code, b, a);
  }

  return (
    <div ref={measured.ref} className={`${s.box} ${className ?? ''}`}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className={`${s.map} ${s[variant]}`}
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      >
        {variant !== 'thumb' && <rect width={width} height={height} className={s.water} />}
        {landPath && <path d={landPath} className={s.land} />}
        {!bare &&
          legs.map((l) => {
            const a = geom.proj(l.from);
            const b = geom.proj(l.to);
            return l.kind === 'flight' ? (
              <RouteArc
                key={l.itemId}
                from={a}
                to={b}
                draw={draw}
                strokeWidth={stroke}
                dots={false}
              />
            ) : (
              <path
                key={l.itemId}
                d={arcPath(a, b, 0.06)}
                className={s.ground}
                strokeWidth={stroke}
                fill="none"
              />
            );
          })}
        {!bare &&
          stops.map((p, i) => {
            const [x, y] = geom.proj(p);
            return <circle key={`s${i}`} cx={x} cy={y} r={stroke * 1.8} className={s.stop} />;
          })}
        {!bare &&
          [...codes.values()].map(({ at, code, pull }) => (
            <g key={code}>
              <circle cx={at[0]} cy={at[1]} r={stroke * 2.1} className={s.node} />
              {labels && (
                <text
                  x={at[0]}
                  y={pull < 0 ? at[1] + 22 : at[1] - 10}
                  className={s.label}
                  textAnchor="middle"
                >
                  {code}
                </text>
              )}
            </g>
          ))}
        {overlay?.(geom.proj, { width, height })}
      </svg>
    </div>
  );
}
